import React, { useState } from "react";
import styled from "styled-components";
import { C2Text, C4Text } from "../text/CustomText";
import { BaseButton, Row, SizedBox } from "../CommonBaseComponents";
import { Theme } from "../../Theme";
import CustomIcon from "../image/CustomIcon";
import Translate from "../../translate/Translate";

const AccordionContainer = styled.div`
    width: 100%;
    margin: 20px auto;
    font-family: "Pretendard", sans-serif;
    overflow: hidden;
`;

const QuestionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background-color: ${Theme.color.white};
    cursor: pointer;
    border: ${(props) => (props.isOpen ? `1px solid ${Theme.color.indigo500}` : `1px solid ${Theme.color.gray200}`)};
    border-bottom: ${(props) =>
        props.isOpen
            ? `1px solid ${Theme.color.indigo500}`
            : props.isLastItem
              ? `1px solid ${Theme.color.gray200}`
              : "none"};
    border-top-left-radius: ${(props) => (props.isFirstItem ? "10px" : "0")};
    border-top-right-radius: ${(props) => (props.isFirstItem ? "10px" : "0")};
    border-bottom-left-radius: ${(props) => (props.isOpen ? "0" : props.isLastItem ? "10px" : "0")};
    border-bottom-right-radius: ${(props) => (props.isOpen ? "0" : props.isLastItem ? "10px" : "0")};

    &:hover {
        background-color: ${Theme.color.zinc25};
    }
`;

const QuestionInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 50px; /* Q, 코드, 텍스트 간의 간격 */
`;

const ControlsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const AnswerContent = styled.div`
    padding: 15px 20px;
    background-color: ${Theme.color.neutral100};
    display: flex;
    gap: 15px;
    border-left: 1px solid ${Theme.color.gray200};
    border-right: 1px solid ${Theme.color.gray200};
    border-bottom: ${(props) => (props.isLastItem ? `1px solid ${Theme.color.gray200}` : "none")};
    border-bottom-left-radius: ${(props) => (props.isLastItem ? "10px" : "0")};
    border-bottom-right-radius: ${(props) => (props.isLastItem ? "10px" : "0")};
`;

const AnswerText = styled.p`
    margin: 0 0 0 108px;
    font-size: ${(props) => props.fontSize || Theme.text.c2.fontSize};
    font-family: ${(props) => props.fontFamily || Theme.text.default.fontFamily};
    letter-spacing: ${(props) => props.letterSpacing || Theme.text.default.letterSpacing};
    font-weight: ${(props) => props.fontWeight || Theme.text.default.fontWeight};
    line-height: 1.4;
    color: ${Theme.color.gray800};
`;

// --- React Component ---

/**
 * 개별 아코디언 아이템 컴포넌트
 * @param {string} questionCode - (예: "3E1")
 * @param {string} questionText - 질문 내용
 * @param {string} answerText - 답변 내용
 * @param {boolean} initialOpen - 처음부터 열려있을지 여부
 * @param isFirstItem - 첫번째 요소
 * @param isLastItem - 마지막 요소
 * @param guideType - 가이드 타입
 */
const AccordionItem = ({
    questionCode,
    questionText,
    answerText,
    initialOpen = false,
    isFirstItem,
    isLastItem,
    guideType = "LG",
}) => {
    const [isOpen, setIsOpen] = useState(initialOpen);

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        console.log(`삭제 버튼 클릭됨`);
    };

    const handleModifyClick = (e) => {
        e.stopPropagation();
        console.log(`수정 버튼 클릭됨`);
    };

    return (
        <>
            {/* 질문 헤더: 클릭 시 isOpen 상태를 토글합니다. */}
            <QuestionHeader
                onClick={() => setIsOpen(!isOpen)}
                isOpen={isOpen}
                isLastItem={isLastItem}
                isFirstItem={isFirstItem}
            >
                <QuestionInfo>
                    <SizedBox
                        padding={"10px 14px"}
                        width={"auto"}
                        backgroundColor={guideType === "LG" ? Theme.color.indigo500_007 : Theme.color.gray500_007}
                        borderRadius={"80px"}
                    >
                        <C2Text
                            fontColor={guideType === "LG" ? Theme.color.indigo500 : Theme.color.gray500}
                            fontWeight={"500"}
                        >
                            {guideType}
                        </C2Text>
                    </SizedBox>
                    <C2Text fontWeight={"500"}>{questionCode}</C2Text>
                    <C2Text fontWeight={"500"}>{questionText}</C2Text>
                </QuestionInfo>

                <ControlsWrapper>
                    <BaseButton
                        width={"36px"}
                        height={"36px"}
                        onClick={() => setIsOpen(!isOpen)}
                        hoverBackgroundColor={Theme.color.neutral100}
                    >
                        <CustomIcon
                            iconName={isOpen ? "chevron_up" : "chevron_down"}
                            size={"16px"}
                            cursor={"pointer"}
                        />
                    </BaseButton>
                    <SizedBox width={"20px"} />
                    <BaseButton
                        padding={"10px 10px"}
                        borderRadius={"8px"}
                        onClick={handleModifyClick}
                        hoverBackgroundColor={Theme.color.neutral100}
                    >
                        <Row alignItem={"center"} justifyContent={"center"} cursor={"pointer"}>
                            <CustomIcon iconName={"edit"} size={"14px"} cursor={"pointer"} />
                            <SizedBox width={"4px"} cursor={"pointer"} />
                            <C4Text cursor={"pointer"}>{Translate("common.edit")}</C4Text>
                        </Row>
                    </BaseButton>
                    <BaseButton
                        padding={"10px 15px"}
                        borderRadius={"8px"}
                        onClick={handleDeleteClick}
                        hoverBackgroundColor={Theme.color.neutral100}
                    >
                        <C4Text cursor={"pointer"}>{Translate("common.delete")}</C4Text>
                    </BaseButton>
                </ControlsWrapper>
            </QuestionHeader>

            {/* 답변 콘텐츠: isOpen이 true일 때만 렌더링됩니다. */}
            {isOpen && (
                <AnswerContent isLastItem={isLastItem}>
                    <Row>
                        <SizedBox padding={"10px 14px"} width={"auto"} borderRadius={"80px"}>
                            <C2Text fontColor={Theme.color.indigo500} fontWeight={"500"}>
                                A.
                            </C2Text>
                        </SizedBox>
                    </Row>
                    <AnswerText>{answerText}</AnswerText>
                </AnswerContent>
            )}
        </>
    );
};

function CustomAccordion({ data }) {
    return (
        <AccordionContainer>
            {data.map((item, index) => (
                <AccordionItem
                    key={index}
                    questionCode={item.qCode}
                    questionText={item.qText}
                    answerText={item.aText}
                    initialOpen={false}
                    isFirstItem={index === 0}
                    isLastItem={index === data.length - 1}
                    guideType={item.guideType}
                />
            ))}
        </AccordionContainer>
    );
}

export default CustomAccordion;
